import Table from '../../../atoms/Table/Table';
import MarkdownContainer from '../../../atoms/MarkdownContainer/MarkdownContainer';
import cn from '../../../../utils/cn';

import GridCell, { getCellColId, getCellRowId } from './GridCell';
import styles from './GridField.module.css';

const AUTO_COL_MIN_WIDTH = 80;

/**
 * Flags for rendering column separators.
 * @param {FormTableHeader[]} headers
 * @returns {{ showDivider: boolean }[]}
 */
function getHeaderGroupFlags(headers) {
  return headers.map((_header, index) => ({
    // A un sens visuel "diviseur" partout sauf à la première colonne.
    showDivider: index > 0,
  }));
}

/**
 * Minimum table width: declared px widths + floor for auto columns.
 * @param {FormTableHeader[]} headers
 * @returns {number}
 */
function getTableMinWidth(headers) {
  return headers.reduce((total, header) => {
    const px = /^(\d+)px$/.exec(header.width ?? '');
    return total + (px ? Number(px[1]) : AUTO_COL_MIN_WIDTH);
  }, 0);
}

/**
 * @param {FormTableCell | null | undefined} cell
 * @returns {boolean}
 */
function isFieldCell(cell) {
  return Boolean(cell?.field?.formItem);
}

/**
 * @param {{ showDivider: boolean }} flags
 * @param {'header' | 'body'} variant
 * @param {FormTableCell | null | undefined} [cell]
 * @returns {string}
 */
function getColumnClassName(flags, variant, cell) {
  const base = variant === 'header' ? styles['table-header-cell'] : styles['table-body-cell'];
  return cn([
    base,
    flags.showDivider && styles['table-divider'],
    variant === 'body' && (isFieldCell(cell) ? styles['is-field-cell'] : styles['is-display-cell']),
  ]);
}

/**
 * Resolve row id from contract rowId or first cell payload.
 * @param {FormTableRow} row
 * @param {number} groupIndex
 * @param {number} rowIndex
 * @returns {string}
 */
function resolveRowId(row, groupIndex, rowIndex) {
  if (row.rowId) return row.rowId;
  const firstCell = (row.cells ?? []).find((c) => getCellRowId(c));
  return getCellRowId(firstCell) || `row-${groupIndex}-${rowIndex}`;
}

/**
 * Grid form field: table layout of display cells and nested form inputs.
 * The grid itself has no RHF value; nested fields register under their own ids.
 * @param {object} props
 * @param {FormTableData} props.tableData
 * @param {(formItem: FormItem) => JSX.Element | null} props.renderNestedField
 * @param {{ getCellValue?: (rowId: string, colId: string) => string | undefined, isRowVisible?: (row: FormTableRow) => boolean }} [props.computed]
 * @returns {JSX.Element | null}
 */
function GridField({ tableData, renderNestedField, computed }) {
  if (!tableData) return null;

  const { headers = [], groups = [], footers = [] } = tableData;
  const colCount = headers.length;
  const headerGroupFlags = getHeaderGroupFlags(headers);
  const getCellValue = computed?.getCellValue;
  const isRowVisible = computed?.isRowVisible ?? (() => true);

  /**
   * @param {FormTableRow} row
   * @param {string} rowKey
   * @param {number} groupIndex
   * @param {number} rowIndex
   * @returns {JSX.Element}
   */
  const renderRow = (row, rowKey, groupIndex, rowIndex) => {
    const rowId = resolveRowId(row, groupIndex, rowIndex);

    return (
      <tr key={rowKey}>
        {headers.length > 0
          ? headers.map((header, headerIndex) => {
            const cell = (row.cells ?? []).find(
              (c) => getCellColId(c) === header.colId,
            );
            const effectiveRowId = row.rowId ?? getCellRowId(cell) ?? rowId;
            return (
              <td
                key={`${rowKey}-${header.colId}`}
                className={getColumnClassName(
                  headerGroupFlags[headerIndex],
                  'body',
                  cell,
                )}
                style={{
                  textAlign: /** @type {'left'|'center'|'right'} */ (
                    header.align || 'left'
                  ),
                  backgroundColor: cell?.text?.backgroundColor || undefined,
                }}
              >
                {cell ? (
                  <GridCell
                    cell={cell}
                    renderNestedField={renderNestedField}
                    getCellValue={getCellValue}
                    rowId={effectiveRowId}
                  />
                ) : null}
              </td>
            );
          })
          : (row.cells ?? []).map((cell, cellIndex) => {
            const colId = getCellColId(cell) || `col-${cellIndex}`;
            const effectiveRowId = row.rowId ?? getCellRowId(cell) ?? rowId;
            return (
              <td
                key={`${rowKey}-${colId}`}
                className={cn([
                  styles['table-body-cell'],
                  isFieldCell(cell)
                    ? styles['is-field-cell']
                    : styles['is-display-cell'],
                ])}
                style={
                  cell?.text?.backgroundColor
                    ? { backgroundColor: cell.text.backgroundColor }
                    : undefined
                }
              >
                <GridCell
                  cell={cell}
                  renderNestedField={renderNestedField}
                  getCellValue={getCellValue}
                  rowId={effectiveRowId}
                />
              </td>
            );
          })}
      </tr>
    );
  };

  return (
    <div
      className={styles['grid-field-wrapper']}
      style={/** @type {import('react').CSSProperties} */ ({
        '--grid-table-min-width': `${getTableMinWidth(headers)}px`,
      })}
    >
      <Table width="100%">
        <>
          {headers.length > 0 && (
            <colgroup>
              {headers.map((header) => (
                <col
                  key={header.colId}
                  style={{ width: header.width || undefined }}
                />
              ))}
            </colgroup>
          )}

          {headers.length > 0 && (
            <thead>
              <tr>
                {headers.map((header, headerIndex) => (
                  <th
                    key={header.colId}
                    className={getColumnClassName(headerGroupFlags[headerIndex], 'header')}
                    style={{
                      textAlign: /** @type {'left'|'center'|'right'} */ (header.align || 'left'),
                      backgroundColor: header.backgroundColor || undefined,
                    }}
                  >
                    <MarkdownContainer content={header.value} />
                  </th>
                ))}
              </tr>
            </thead>
          )}

          {groups.map((group, groupIndex) => (
            <tbody key={group.lib || `group-${groupIndex}`}>
              {group.lib ? (
                <tr>
                  <td className={styles['table-group-header']} colSpan={colCount || 1}>
                    {group.lib}
                  </td>
                </tr>
              ) : null}

              {(group.rows ?? []).flatMap((row, rowIndex) => {
                if (!isRowVisible(row)) return [];

                const rowKey = row.rowId || `row-${groupIndex}-${rowIndex}`;
                const rows = [renderRow(row, rowKey, groupIndex, rowIndex)];

                (row.subRows ?? []).forEach((subRow, subIndex) => {
                  if (!isRowVisible(subRow)) return;
                  const subKey = subRow.rowId || `${rowKey}-sub-${subIndex}`;
                  rows.push(renderRow(subRow, subKey, groupIndex, rowIndex));
                });

                return rows;
              })}
            </tbody>
          ))}

          {footers.length > 0 && (
            <tfoot>
              <tr>
                {footers.map((footer) => (
                  <td
                    key={`footer-${footer.value}-${footer.align || 'left'}`}
                    className={styles['table-footer-cell']}
                    style={{
                      textAlign: /** @type {'left'|'center'|'right'} */ (footer.align || 'left'),
                    }}
                  >
                    <MarkdownContainer content={footer.value} />
                  </td>
                ))}
              </tr>
            </tfoot>
          )}
        </>
      </Table>
    </div>
  );
}

export default GridField;
